import { useEffect, useRef, useState, useCallback } from 'react';
import fire5Sprite from '../Moves/fire5.png';
import explosionSprite from '../Moves/PRAS- Explosions.png';
import './FireBlastAnimation.css';

/**
 * PROFESSIONAL FIRE BLAST ANIMATION - REDESIGNED
 * Fire-type move with authentic Pokémon-style 3-phase sequence
 * 
 * Key Features:
 * - Uses fire5.png sprite (960x576, 192x192 frames)
 * - Phase 1: 5 flame sprites rotate around player (power-up)
 * - Phase 2: Flames converge into projectile ball toward opponent
 * - Phase 3: Impact creates Kanji symbol flash effect like authentic Pokémon
 * - Professional gaming company quality with proper timing
 */
export default function FireBlastAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  const [animationPhase, setAnimationPhase] = useState('idle');
  
  // SPRITE CONFIGURATIONS
  const FIRE5_CONFIG = {
    frameWidth: 192,
    frameHeight: 192,
    spriteWidth: 960,
    spriteHeight: 576,
    // Frame 5 (index 4): Large intense flame for power-up (row 1, col 5)
    frame5: { x: 768, y: 0 }, // -768px 0px
    // Frame 6 (index 5): Aftershock flame (row 2, col 1)
    frame6: { x: 0, y: 192 }, // 0px -192px
    // Frame 8 (index 7): Medium flame for projectile (row 2, col 3) 
    frame8: { x: 384, y: 192 }, // -384px -192px
    // Frame 9 (index 8): Intense flame for impact (row 2, col 4)
    frame9: { x: 576, y: 192 } // -576px -192px
  };
  
  const EXPLOSION_CONFIG = {
    frameWidth: 192,
    frameHeight: 192,
    spriteWidth: 768,
    spriteHeight: 576,
    // Explosion frames 0-3 (top row)
    frame0: { x: 0, y: 0 },     // 0px 0px
    frame1: { x: 192, y: 0 },   // -192px 0px
    frame2: { x: 384, y: 0 },   // -384px 0px
    frame3: { x: 576, y: 0 }    // -576px 0px
  };
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;
    
    const container = document.querySelector('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerCenterX = playerRect.left + playerRect.width / 2 - containerRect.left;
    const playerCenterY = playerRect.top + playerRect.height / 2 - containerRect.top;
    
    // Target the actual visible sprite, not the wrapper
    const targetEl = opponentEl.querySelector?.('.wild-front') || opponentEl;
    const targetRect = targetEl.getBoundingClientRect();
    
    // Apply stronger left bias to correct right offset - adjustable per sprite
    const biasX = Number(targetEl.dataset?.hitCenterBiasX ?? opponentEl.dataset?.hitCenterBiasX ?? -25);
    const biasY = Number(targetEl.dataset?.hitCenterBiasY ?? opponentEl.dataset?.hitCenterBiasY ?? 0);
    
    const opponentCenterX = targetRect.left + targetRect.width / 2 - containerRect.left + biasX;
    const opponentCenterY = targetRect.top + targetRect.height / 2 - containerRect.top + biasY;
    
    const cleanupElements = [];
    const cleanupTimers = [];
    
    console.log(' Fire Blast Animation Starting - New Design!');
    
    // PHASE 1: POWER-UP - 5 flame sprites rotate around player (0-800ms)
    const powerUpTimer = setTimeout(() => {
      setAnimationPhase('powerup');
      console.log(' Phase 1: Power-Up - 5 flames rotating around player');
      
      // Add charging effect to player
      if (playerEl) {
        playerEl.classList.add('fireblast-charge');
      }
      
      // Create 5 flame sprites around player using frame 5
      const flameCount = 5;
      const radius = 60; // Distance from player center
      
      for (let i = 0; i < flameCount; i++) {
        const angle = (i / flameCount) * Math.PI * 2;
        const angleDeg = Math.round((angle * 180) / Math.PI);
        
        setTimeout(() => {
          const flame = document.createElement('div');
          flame.className = 'fireblast-flame fireblast-powerup';
          flame.style.left = `${playerCenterX}px`;
          flame.style.top = `${playerCenterY}px`;
          flame.style.width = `${FIRE5_CONFIG.frameWidth}px`;
          flame.style.height = `${FIRE5_CONFIG.frameHeight}px`;
          flame.style.backgroundImage = `url(${fire5Sprite})`;
          flame.style.backgroundSize = `${FIRE5_CONFIG.spriteWidth}px ${FIRE5_CONFIG.spriteHeight}px`;
          flame.style.backgroundPosition = `-${FIRE5_CONFIG.frame5.x}px -${FIRE5_CONFIG.frame5.y}px`;
          
          // Set CSS variables for rotation
          flame.style.setProperty('--angle-start', `${angleDeg}deg`);
          flame.style.setProperty('--radius', `${radius}px`);
          flame.style.setProperty('--rotation-delay', `${i * 0.1}s`);
          
          container.appendChild(flame);
          cleanupElements.push(flame);
          
          console.log(`🔥 Flame ${i + 1} created at angle ${angleDeg}°`);
        }, i * 50); // Staggered appearance
      }
      
    }, 0);
    cleanupTimers.push(powerUpTimer);
    
    // PHASE 2: PROJECTILE - Flames converge into ball toward opponent (800-1400ms)
    const projectileTimer = setTimeout(() => {
      setAnimationPhase('projectile');
      console.log('🔥 Phase 2: Projectile - Flames converging toward opponent');
      
      const flames = container.querySelectorAll('.fireblast-flame');
      flames.forEach((flame, index) => {
        flame.classList.remove('fireblast-powerup');
        flame.classList.add('fireblast-projectile');
        
        // Change to frame 8 for projectile
        flame.style.backgroundPosition = `-${FIRE5_CONFIG.frame8.x}px -${FIRE5_CONFIG.frame8.y}px`;
        
        // Animate convergence to center, then toward opponent
        setTimeout(() => {
          flame.style.transition = 'left 0.3s ease-in, top 0.3s ease-in, transform 0.3s ease-in';
          flame.style.left = `${playerCenterX}px`;
          flame.style.top = `${playerCenterY}px`;
          flame.style.transform = 'translate(-50%, -50%) scale(0.8)';
          
          // After convergence, launch toward opponent
          setTimeout(() => {
            flame.style.transition = 'left 0.4s ease-out, top 0.4s ease-out, transform 0.4s ease-out';
            flame.style.left = `${opponentCenterX}px`;
            flame.style.top = `${opponentCenterY}px`;
            flame.style.transform = 'translate(-50%, -50%) scale(1.0)';
          }, 300);
        }, index * 30); // Slight stagger
      });
      
    }, 800);
    cleanupTimers.push(projectileTimer);
    
    // PHASE 3: IMPACT & EXPLOSION - Professional explosion sequence (1400-2200ms)
    const impactTimer = setTimeout(() => {
      setAnimationPhase('impact');
      console.log('🔥 Phase 3: Impact - Professional explosion sequence');
      
      // Remove projectile flames
      const flames = container.querySelectorAll('.fireblast-flame');
      flames.forEach(flame => {
        flame.style.animation = 'fireblastFadeOut 0.2s ease-out forwards';
        setTimeout(() => flame.remove(), 200);
      });
      
      // Create explosion sequence using PRAS-Explosions.png frames 0-3
      const explosionFrames = [0, 1, 2, 3];
      let frameIndex = 0;
      
      const createExplosionFrame = () => {
        if (frameIndex < explosionFrames.length) {
          const currentFrame = explosionFrames[frameIndex];
          const frameKey = `frame${currentFrame}`;
          const frame = EXPLOSION_CONFIG[frameKey];
          
          const explosion = document.createElement('div');
          explosion.className = 'fireblast-explosion';
          explosion.style.left = `${opponentCenterX}px`;
          explosion.style.top = `${opponentCenterY}px`;
          explosion.style.width = `${EXPLOSION_CONFIG.frameWidth}px`;
          explosion.style.height = `${EXPLOSION_CONFIG.frameHeight}px`;
          explosion.style.backgroundImage = `url(${explosionSprite})`;
          explosion.style.backgroundSize = `${EXPLOSION_CONFIG.spriteWidth}px ${EXPLOSION_CONFIG.spriteHeight}px`;
          explosion.style.backgroundPosition = `-${frame.x}px -${frame.y}px`;
          explosion.style.animation = 'fireblastExplosionFrame 0.15s ease-out forwards';
          
          container.appendChild(explosion);
          cleanupElements.push(explosion);
          
          console.log(`🔥 Explosion Frame ${currentFrame} (${frameIndex + 1}/${explosionFrames.length})`);
          
          // Remove previous explosion frame
          if (frameIndex > 0) {
            const prevExplosions = container.querySelectorAll('.fireblast-explosion');
            if (prevExplosions.length > 1) {
              prevExplosions[0].remove();
            }
          }
          
          frameIndex++;
          setTimeout(createExplosionFrame, 150);
        } else {
          // Add aftershock flash using frame 6
          setTimeout(() => {
            const aftershock = document.createElement('div');
            aftershock.className = 'fireblast-aftershock';
            aftershock.style.left = `${opponentCenterX}px`;
            aftershock.style.top = `${opponentCenterY}px`;
            aftershock.style.width = `${FIRE5_CONFIG.frameWidth}px`;
            aftershock.style.height = `${FIRE5_CONFIG.frameHeight}px`;
            aftershock.style.backgroundImage = `url(${fire5Sprite})`;
            aftershock.style.backgroundSize = `${FIRE5_CONFIG.spriteWidth}px ${FIRE5_CONFIG.spriteHeight}px`;
            aftershock.style.backgroundPosition = `-${FIRE5_CONFIG.frame6.x}px -${FIRE5_CONFIG.frame6.y}px`;
            aftershock.style.animation = 'fireblastAftershock 0.4s ease-out forwards';
            
            container.appendChild(aftershock);
            cleanupElements.push(aftershock);
            
            console.log('🔥 Aftershock flash effect');
          }, 100);
        }
      };
      
      createExplosionFrame();
      
      // Screen flash effect
      setTimeout(() => {
        const screenFlash = document.createElement('div');
        screenFlash.className = 'fireblast-screen-flash';
        screenFlash.style.position = 'fixed';
        screenFlash.style.top = '0';
        screenFlash.style.left = '0';
        screenFlash.style.width = '100vw';
        screenFlash.style.height = '100vh';
        screenFlash.style.background = 'rgba(255, 120, 0, 0.4)';
        screenFlash.style.pointerEvents = 'none';
        screenFlash.style.zIndex = '1000';
        screenFlash.style.animation = 'fireblastScreenFlash 0.3s ease-out';
        container.appendChild(screenFlash);
        cleanupElements.push(screenFlash);
        
        // Opponent hit reaction
        if (opponentEl) {
          opponentEl.classList.add('fireblast-hit');
          setTimeout(() => {
            opponentEl.classList.remove('fireblast-hit');
          }, 400);
        }
        
        // Complete animation
        setTimeout(() => {
          console.log('🔥 Fire Blast Animation Complete - Professional Design!');
          onComplete();
        }, 500);
      }, 300);
      
    }, 1400);
    cleanupTimers.push(impactTimer);
    
    // Cleanup function
    return () => {
      // Remove all classes from player and opponent
      if (playerEl) {
        playerEl.classList.remove('fireblast-charge');
      }
      if (opponentEl) {
        opponentEl.classList.remove('fireblast-hit');
      }
      
      // Remove all created elements
      cleanupElements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Clear all timers
      cleanupTimers.forEach(timer => clearTimeout(timer));
      
      console.log('🔥 Fire Blast Animation Cleanup Complete');
    };
    
  }, [playerRef, opponentRef, onComplete]);
  
  return null; // This component only manages DOM manipulation
}
